import { HttpErrorResponse } from '@angular/common/http';
import { AccessContextApiService } from '../../api/access-context-api.service';
import type { AccessContext } from '../../interfaces/access-context';
import {
  inject,
  Injectable,
  InjectionToken,
  signal,
  DestroyRef,
} from '@angular/core';
import Keycloak, { KeycloakTokenParsed } from 'keycloak-js';
import { AccessClaims } from './access-claims';
import { Subject } from 'rxjs';
import { EStorageKeys } from '@app/app.enums';
import { localReturnPath, RUNTIME_CONFIG } from '../../config/runtime-config';

const RETURN_PATH = 'mt-auth-oidc-return-path';

export const KEYCLOAK_CLIENT = new InjectionToken<Keycloak>('Keycloak client', {
  providedIn: 'root',
  factory: () => {
    const config = inject(RUNTIME_CONFIG);
    return new Keycloak({
      url: new URL(config.keycloakUrl, location.origin).href.replace(/\/$/, ''),
      realm: config.realm,
      clientId: config.clientId,
    });
  },
});

@Injectable({ providedIn: 'root' })
export class KeycloakSessionService {
  readonly revision = signal(0);
  private readonly contextApi = inject(AccessContextApiService);
  private accessContext?: AccessContext;
  private contextToken?: string;
  private pendingContext?: { token: string; request: Promise<void> };
  private closed = false;
  private signingOut = false;
  private readonly config = inject(RUNTIME_CONFIG);
  private readonly serverUrl = new URL(
    this.config.keycloakUrl,
    location.origin,
  ).href.replace(/\/$/, '');
  private readonly client = inject(KEYCLOAK_CLIENT);
  private readonly logoutChannel =
    typeof BroadcastChannel === 'undefined'
      ? null
      : new BroadcastChannel(
          `mt-auth-logout:${this.serverUrl}:${this.config.realm}:${this.config.clientId}`,
        );
  private readonly sessionEnded = new Subject<void>();
  readonly ended$ = this.sessionEnded.asObservable();
  readonly realmUrl = `${this.serverUrl}/realms/${encodeURIComponent(this.config.realm)}`;
  readonly iamUrl = `${this.realmUrl}/mt-iam`;
  readonly adminUrl = `${this.serverUrl}/admin/realms/${encodeURIComponent(this.config.realm)}`;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.closed = true;
      this.client.onTokenExpired = undefined;
      this.logoutChannel?.close();
      this.sessionEnded.complete();
    });
  }
  get authenticated(): boolean {
    this.revision();
    return !this.closed && this.client.authenticated === true;
  }
  get claims(): (KeycloakTokenParsed & AccessClaims) | undefined {
    this.revision();
    return (
      this.client.tokenParsed && {
        ...this.client.tokenParsed,
        ...(this.contextToken === this.client.token ? this.accessContext : {}),
      }
    );
  }
  get token(): string | undefined {
    return this.client.token;
  }
  get ending(): boolean {
    return this.signingOut;
  }
  get isSecurityAdministrator(): boolean {
    return this.claims?.['isSecurityAdministrator'] === true;
  }

  get returnPath(): string {
    return (
      localReturnPath(sessionStorage.getItem(RETURN_PATH), location.origin) ??
      '/main'
    );
  }

  async initialize(): Promise<boolean> {
    for (const key of [
      EStorageKeys.AUTH_TOKENS,
      EStorageKeys.AUTH_USER,
      EStorageKeys.CODE_VERIFIER,
      EStorageKeys.STATE_FOR_AUTHORIZE,
      EStorageKeys.REDIRECT_TO,
      EStorageKeys.REDIRECT_AUTH_TO,
    ]) {
      localStorage.removeItem(key);
    }
    const current = new URL(location.href);
    const returnPath = localReturnPath(
      current.pathname === '/administration-required'
        ? current.searchParams.get('returnPath')
        : current.pathname + current.search + current.hash,
      location.origin,
    );
    if (
      returnPath &&
      (current.pathname !== '/' ||
        !localReturnPath(sessionStorage.getItem(RETURN_PATH), location.origin))
    )
      sessionStorage.setItem(RETURN_PATH, returnPath);
    this.client.onAuthRefreshSuccess = () => {
      void this.publish().catch(() => undefined);
    };
    this.client.onTokenExpired = () => {
      void this.accessToken().catch(() => undefined);
    };
    this.client.onAuthLogout = () => {
      this.closed = true;
      this.contextToken = undefined;
      this.accessContext = undefined;
      this.pendingContext = undefined;
      this.revision.update((value) => value + 1);
      this.sessionEnded.next();
    };
    this.client.onAuthRefreshError = () => {
      if (!this.authenticated) this.revision.update((value) => value + 1);
    };
    if (this.logoutChannel)
      this.logoutChannel.onmessage = () => this.client.clearToken();
    const authenticated = await this.client.init({
      onLoad: 'check-sso',
      pkceMethod: 'S256',
      flow: 'standard',
      responseMode: 'query',
      redirectUri: `${location.origin}/auth-code`,
      checkLoginIframe: true,
      scope: 'openid',
      locale: 'ru',
    });
    if (authenticated) await this.accessToken();
    this.revision.update((value) => value + 1);
    return authenticated;
  }

  async accessToken(forceRefresh = false): Promise<string> {
    if (!this.authenticated) throw new Error('Сеанс завершён');
    await this.client.updateToken(forceRefresh ? -1 : 30);

    if (!this.client.token) throw new Error('Сеанс завершён');
    this.validateIdentity();
    const sentToken = this.client.token;
    try {
      await this.publish();
    } catch (error: unknown) {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401)
        throw error;
      await this.client.updateToken(sentToken === this.client.token ? -1 : 30);
      await this.publish();
    }
    if (!this.authenticated || !this.client.token)
      throw new Error('Сеанс завершён');
    return this.client.token;
  }

  async confirmActiveSession(): Promise<void> {
    if (this.ending) throw new Error('Сеанс завершён');
    await this.accessToken(true);
    if (this.ending) throw new Error('Сеанс завершён');
  }

  private async publish(): Promise<void> {
    if (!this.authenticated || !this.client.token) return;
    this.validateIdentity();
    const token = this.client.token;
    if (this.contextToken === token) return;
    if (this.pendingContext?.token === token)
      return this.pendingContext.request;
    const request = this.contextApi
      .read(token)
      .then(async (context) => {
        if (!this.authenticated) return;
        if (this.client.token !== token) return this.publish();
        this.accessContext = context;
        this.contextToken = token;
        this.revision.update((value) => value + 1);
      })
      .catch((error: unknown) => {
        if (this.authenticated && this.client.token !== token)
          return this.publish();
        throw error;
      })
      .finally(() => {
        if (this.pendingContext?.token === token)
          this.pendingContext = undefined;
      });
    this.pendingContext = { token, request };
    return request;
  }

  login(returnPath?: string): Promise<void> {
    const path = localReturnPath(returnPath ?? null, location.origin);
    if (path) sessionStorage.setItem(RETURN_PATH, path);
    return this.client.login({
      redirectUri: `${location.origin}/auth-code`,
      locale: 'ru',
    });
  }

  changePassword(): Promise<void> {
    return this.accountAction('UPDATE_PASSWORD');
  }

  verifyEmail(): Promise<void> {
    return this.accountAction('VERIFY_EMAIL');
  }

  private accountAction(
    action: 'UPDATE_PASSWORD' | 'VERIFY_EMAIL',
  ): Promise<void> {
    sessionStorage.setItem(RETURN_PATH, '/account');
    return this.client.login({
      action,
      locale: 'ru',
      redirectUri: `${location.origin}/auth-code`,
    });
  }

  async logout(returnPath?: string): Promise<void> {
    this.signingOut = true;
    const path = localReturnPath(returnPath ?? null, location.origin);
    if (path) sessionStorage.setItem(RETURN_PATH, path);
    else sessionStorage.removeItem(RETURN_PATH);
    this.logoutChannel?.postMessage('logout');
    await this.client.logout({ redirectUri: `${location.origin}/` });
  }

  private validateIdentity(): void {
    if (
      this.client.tokenParsed?.iss !== this.realmUrl ||
      this.client.tokenParsed?.azp !== this.config.clientId
    ) {
      this.client.clearToken();
      throw new Error('Сеанс получен от другого сервера или клиента');
    }
  }

  consumeReturnPath(): string {
    const path = localReturnPath(
      sessionStorage.getItem(RETURN_PATH),
      location.origin,
    );
    sessionStorage.removeItem(RETURN_PATH);
    return path ?? '/main';
  }
}
